import { searchByHotelIdInElastic } from 'app/api/hotels';
import { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { openInput } from '../../../actions/baseActions';
import { selectHotelDestination } from '../../../actions/hotelActions';
import Helper from '../../modules/Helper';
import AirportSelect from '../Common/AirportSelect';
import { IAirportItem } from '../Common/AirportSelect/Item';
import { mapResolvedDestinationToAirportItem } from '../../modules/DatadirectsMapping';
import { DataDirectsProvider } from '@datadirects/datadirects-platform/react';
import { usePrepopulate } from '@datadirects/datadirects-platform/react';
import { datadirectsClient } from '../../modules/DatadirectsClient';
import ApiInterface from '../../modules/ApiInterface';
import { emitLocationItemSelectGAEvent } from 'utils/ga-events-helpers';

type HotelDestinationProps = {
	name: string;
	autoJump?: boolean;
	preventSetInputBoxClassName?: boolean;
	hotelDestination?: IAirportItem;
	openInput: (name?: string | null) => void;
	selectHotelDestination: (location: IAirportItem | object) => void;
};

const HotelDestinationInner: FC<HotelDestinationProps> = (props) => {
	const { prepopulate, data, loading, error } = usePrepopulate();
	const parsed = Helper.getQueryObject();
	const destcodeFromUrl = Helper.getDestcodeFromQuery(parsed);

	useEffect(() => {
		if (destcodeFromUrl) {
			const locale = Helper.getLanguageCode(String(parsed.lng || parsed.lang || 'en'));
			prepopulate({
				destcode: destcodeFromUrl,
				locale,
				searchParams: window.location.search,
			});
		}
	}, [destcodeFromUrl, prepopulate]);

	const enrichStateFromCityLookup = async (
		destcodeParam: string,
		item: IAirportItem,
	): Promise<IAirportItem> => {
		if (!/^P\d+$/i.test(destcodeParam)) {
			return item;
		}
		if (item.state && String(item.state).trim() !== '') {
			return item;
		}
		try {
			const lng = Helper.getLanguageCode(String(parsed.lng || parsed.lang || 'en'));
			const response = await ApiInterface.instance.fetchCity(destcodeParam, { lng });
			const result = response?.data?.results?.[0];
			const stateFromLookup =
				result?.state?.raw || result?.region?.raw || result?.state_name?.raw || '';
			if (!stateFromLookup || String(stateFromLookup).trim() === '') {
				return item;
			}
			return {
				...item,
				state: String(stateFromLookup),
			};
		} catch {
			return item;
		}
	};

	useEffect(() => {
		setTimeout(() => {
			if (Helper.isEmpty(props.selectHotelDestination)) {
				document.getElementById(props.name + '-input')?.focus();
				// @ts-ignore
				document.getElementById(props.name + '-input')?.select();
			}
			if (parsed.f && parsed.f === 'cz' && parsed.hid) {
				searchHotel(parsed.hid.toString());
			}
		}, 1);
	}, []);

	useEffect(() => {
		if (!destcodeFromUrl) {
			return;
		}
		if (loading) {
			return;
		}
		// Wait until we either have data or a terminal error from prepopulate
		if (!data && !error) {
			return;
		}
		const parsedDestCode = destcodeFromUrl.toString();
		// If SDK prepopulate couldn't resolve destination, try explicit resolveDestination
		(async () => {
			// Happy path: prepopulate resolved destination
			if (data && data.destination) {
				const mappedItem = mapResolvedDestinationToAirportItem(data.destination);
				const item = await enrichStateFromCityLookup(parsedDestCode, mappedItem);
				props.selectHotelDestination({
					...item,
					fromDestcodeParam: true,
				});
				return;
			}
		})();
	}, [data, loading, error, destcodeFromUrl]);

	const searchHotel = async (hid: string) => {
		props.selectHotelDestination(await searchByHotelIdInElastic(hid));
	};

	useEffect(() => {
		if (props.hotelDestination) {
			(document.getElementById(props.name + '-input') as HTMLInputElement).disabled = true;
			(document.getElementById(props.name + '-input') as HTMLInputElement).disabled = false;
		}
	}, [props.hotelDestination, props.name]);

	const onSelect = async (location: IAirportItem | object) => {
		let payload: IAirportItem | object = location;
		const maybeItem = location as IAirportItem;
		if (maybeItem && typeof maybeItem === 'object') {
			// Only resolve if we don't already have a ResolvedDestination and we know destcode
			if (!maybeItem.resolvedDestination && maybeItem.destcode) {
				try {
					const result = await datadirectsClient.hotels.prepopulate({
						destcode: maybeItem.destcode,
						locale: Helper.getLanguageCode(String(parsed.lng || parsed.lang || 'en')),
					});
					if (result.destination) {
						payload = {
							...maybeItem,
							resolvedDestination: result.destination,
						};
					}
				} catch (error) {
					console.error('datadirectsClient.hotels.prepopulate failed for selection', error);
				}
			}
		}

		props.selectHotelDestination(payload);

		// GA4: fire location_item_select only for a real suggestion selection. Clearing the
		// desktop input calls onSelect({}) (SHP-206), which previously emitted the event with
		// an empty payload — guard against that.
		if (maybeItem && !Helper.isEmpty(maybeItem)) {
			const { resolvedDestination: _omit, ...suggestionData } = maybeItem;
			emitLocationItemSelectGAEvent(suggestionData);
		}

		if (props.autoJump) {
			props.openInput('check-in-date');
		} else {
			props.openInput(null);
		}
	};

	return (
		<>
			<AirportSelect
				mobileHeading={Helper.trans('main.hotels.goingTo')}
				// i18nValidationDropDownKey={'validation.cars.pickUpLocationSelectDropDown'}
				preventSetInputBoxClassName={props.preventSetInputBoxClassName}
				icon="/images/hotels/marker-icon.svg"
				placeholder={Helper.trans('main.hotels.inputPlaceholder')}
				selected={props.hotelDestination}
				name={props.name}
				onSelect={onSelect}
			/>
		</>
	);
};

const HotelDestination: FC<HotelDestinationProps> = (props) => {
	return (
		<DataDirectsProvider client={datadirectsClient}>
			<HotelDestinationInner {...props} />
		</DataDirectsProvider>
	);
};

// @ts-ignore
const mapStateToProps = (state) => ({
	hotelDestination: state.hotel.hotelDestination,
	autoJump: state.base.autoJump,
});

export default connect(mapStateToProps, {
	selectHotelDestination,
	openInput,
})(HotelDestination);
